import { PaintBrushIcon, PrinterIcon } from "@heroicons/react/24/outline";
import type { ComponentType, SVGProps } from "react";
import { AUDIENCE_ACCENT, AUDIENCE_LABELS, type Audience } from "@/content";

const ORDER: Audience[] = ["imprenta", "broker"];

const ICONS: Record<Audience, ComponentType<SVGProps<SVGSVGElement>>> = {
  imprenta: PrinterIcon,
  broker: PaintBrushIcon,
};

type AudienceToggleProps = {
  value: Audience;
  onChange: (audience: Audience) => void;
  /** Icon-only, no lead-in question — for the sticky nav. */
  compact?: boolean;
};

/**
 * Audience selector. In full mode (hero) a lead-in question + labels make it
 * read as a deliberate "who are you?" choice. In compact mode (nav) it shrinks
 * to two icons that persist while scrolling. Drives the hero, problem,
 * how-it-works and feature set, and recolors the page.
 */
export function AudienceToggle({ value, onChange, compact = false }: AudienceToggleProps) {
  const control = (
    <div
      role="tablist"
      aria-label="¿Con qué te identificas?"
      className={`inline-flex items-center gap-1 rounded-full border border-[var(--separator)] bg-[var(--surface)] ${
        compact ? "p-0.5" : "p-1"
      }`}
    >
      {ORDER.map((audience) => {
        const isActive = value === audience;
        const Icon = ICONS[audience];
        return (
          <button
            key={audience}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={AUDIENCE_LABELS[audience]}
            title={AUDIENCE_LABELS[audience]}
            onClick={() => onChange(audience)}
            className={`flex items-center gap-2 rounded-full font-medium transition-colors ${
              compact ? "p-1.5" : "px-4 py-2 text-sm"
            } ${isActive ? "" : "text-[var(--muted)] hover:text-[var(--foreground)]"}`}
            style={
              isActive
                ? { background: AUDIENCE_ACCENT[audience], color: "var(--accent-foreground)" }
                : {}
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            {!compact && AUDIENCE_LABELS[audience]}
          </button>
        );
      })}
    </div>
  );

  if (compact) return control;

  return (
    <div className="flex flex-col items-center gap-2.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">
        ¿Con qué te identificas?
      </span>
      {control}
    </div>
  );
}
